/**
 * `lerOs` — o PDF da gráfica vira `OsLida`.
 *
 * A versão do leitor é gravada em toda importação. Quando o layout da OS mudar
 * (atualização do sistema da gráfica), o PDF cai na quarentena, o leitor ganha
 * versão nova, e os PDFs parados são reprocessados. É por isso que a versão
 * mora aqui e não num comentário.
 */

import { motivoDeQuarentena, type OsLida } from '@pcp/dominio';
import { lerCabecalho } from './cabecalho.js';
import { lerEtapas } from './etapas.js';
import { extrairImagemJpeg, lerPdf } from './pdf.js';
import { lerPlanos } from './planos.js';
import { lerEntregas, lerEspecificacoes, lerMateriais } from './rodape.js';

/**
 * Versão do contrato de leitura. Sobe quando a estrutura lida muda —
 * seja porque a OS mudou, seja porque o leitor passou a extrair mais.
 */
export const VERSAO_LEITOR = '1.0.0';

export interface ResultadoDaLeitura {
  os: OsLida | null;
  /** Preenchido quando a OS não pode entrar (ANATOMIA §4). */
  motivoQuarentena: string | null;
  avisos: string[];
  /** A imagem da faca, quando o PDF traz uma. */
  imagem: Uint8Array | null;
  /** O texto em ordem de conteúdo, para conferência e diagnóstico. */
  textoEmOrdem: string;
}

/**
 * Normalização antes do hash: espaços colapsados e o rodapé de página fora.
 *
 * O rodapé sai porque ele carrega "Pág: 1/1" e nada mais; mantê-lo não muda
 * nada hoje e criaria diferença falsa se a paginação mudasse numa reimpressão
 * sem alteração de conteúdo.
 */
export function normalizarParaHash(texto: string): string {
  return texto
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0 && !/^Gráfica Boa União\.\s*Pág:/.test(l))
    .join('\n');
}

/** SHA-256 em hexadecimal. Roda igual no navegador e no Node. */
export async function sha256(texto: string): Promise<string> {
  const bytes = new TextEncoder().encode(texto);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function lerOs(dados: Uint8Array): Promise<ResultadoDaLeitura> {
  const avisos: string[] = [];

  let pdf;
  try {
    pdf = await lerPdf(dados);
  } catch (erro) {
    return {
      os: null,
      motivoQuarentena: `Não consegui abrir o arquivo como PDF: ${mensagem(erro)}`,
      avisos,
      imagem: null,
      textoEmOrdem: '',
    };
  }

  if (pdf.itens.length === 0) {
    return {
      os: null,
      motivoQuarentena: 'O PDF não tem texto. Uma OS digitalizada como imagem não pode ser lida.',
      avisos,
      imagem: null,
      textoEmOrdem: '',
    };
  }

  const cabecalho = lerCabecalho(pdf.itens, avisos);
  if (!cabecalho) {
    return {
      os: null,
      motivoQuarentena:
        'Não achei o número da OS nem a data de emissão. O arquivo não parece uma Ordem de Serviço da Gráfica Boa União.',
      avisos,
      imagem: null,
      textoEmOrdem: pdf.textoEmOrdem,
    };
  }

  if (cabecalho.paginas > 1) {
    avisos.push(
      `A OS tem ${cabecalho.paginas} páginas. Nenhuma das 21 OS de amostra tinha mais de uma; confira a leitura.`,
    );
  }

  const planos = lerPlanos(pdf.itens, avisos);
  const etapas = lerEtapas(pdf.itens, avisos);
  const especificacoes = lerEspecificacoes(pdf.itens, avisos);
  const materiais = lerMateriais(pdf.itens, avisos);
  const entregas = lerEntregas(pdf.itens);
  const imagem = extrairImagemJpeg(dados);

  const tempoPrevistoTotalSeg = etapas.reduce((s, e) => s + (e.tempoPrevistoSeg ?? 0), 0);
  const hashTexto = await sha256(normalizarParaHash(pdf.textoEmOrdem));

  const os: OsLida = {
    cabecalho,
    planos,
    etapas,
    especificacoes,
    materiais,
    entregas,
    avisos,
    tempoPrevistoTotalSeg,
    hashTexto,
    versaoLeitor: VERSAO_LEITOR,
    temImagem: imagem !== null,
  };

  return {
    os,
    motivoQuarentena: motivoDeQuarentena(os),
    avisos,
    imagem,
    textoEmOrdem: pdf.textoEmOrdem,
  };
}

function mensagem(erro: unknown): string {
  if (erro instanceof Error) return erro.message;
  return String(erro);
}
